import { DestroyRef, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { InsightsService as GeneratedInsightsService } from '../../api';
import { BrowserEnvironmentService } from '../browser-environment.service';
import { HostApplicationService } from '../services/host-application.service';
import { QueryParamTokenService } from './query-param-token.service';

/** Delay between host token requests while the host frame is still starting up. */
export const EMBEDDED_TOKEN_RETRY_MS = 250;

/** Upper bound on how long to wait for the host frame to supply a token. */
export const EMBEDDED_TOKEN_TIMEOUT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class KeycloakAuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browserEnvironment = inject(BrowserEnvironmentService);
  private readonly hostApplicationService = inject(HostApplicationService);
  private readonly insightsApi = inject(GeneratedInsightsService);
  private readonly queryParamTokenService = inject(QueryParamTokenService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private initPromise: Promise<void> | null = null;
  private isInitialised = false;
  private externalToken: string | null = null;

  readonly isAuthenticated = signal(false);

  constructor() {
    this.hostApplicationService.onAuthTokenUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((token) => {
      this.externalToken = token;

      if (token) {
        this.isAuthenticated.set(true);
      }
    });
  }

  isEnabled(): boolean {
    return this.isBrowser;
  }

  async initialise(): Promise<void> {
    if (!this.isEnabled() || this.isInitialised) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.initialiseInternal();
    return this.initPromise;
  }

  async ensureAuthenticated(redirectPath?: string): Promise<boolean> {
    if (this.queryParamTokenService.hasToken()) {
      this.isAuthenticated.set(true);
      return true;
    }

    await this.initialise();

    if (this.externalToken) {
      this.isAuthenticated.set(true);
      return true;
    }

    void redirectPath;
    // Authentication challenge and JWT validation happen on the API.
    // The UI only forwards a host token when one is available.
    return this.isEnabled();
  }

  async getValidToken(minValiditySeconds = 30): Promise<string | null> {
    await this.initialise();
    void minValiditySeconds;

    // Query parameter token intentionally takes precedence for this SPA session.
    return this.queryParamTokenService.getToken() ?? this.externalToken;
  }

  async isApiAuthorised(): Promise<boolean> {
    await this.getValidToken();

    try {
      // Any authenticated route passes the auth and access-flag middleware; categories is the cheapest.
      const response = await firstValueFrom(
        this.insightsApi.listInsightCategoriesInsightsCategoriesGet(
          'response',
          false,
          {
            transferCache: false,
          }
        )
      );

      return response.status === 200;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        return false;
      }

      return false;
    }
  }

  /**
   * Determine if an Authorization header should be attached to a request.
   *
   * Authentication is mandatory in browser contexts.
   *
   * The UI attaches bearer tokens to API requests when available from:
   * - Query parameter token (captured in-memory)
   * - Embedded host application token
   */
  shouldAttachToken(requestUrl: string): boolean {
    // Browser auth is always enabled; tokens may come from multiple sources.
    const hasAuthSource = this.isEnabled() || this.externalToken || this.queryParamTokenService.hasToken();
    if (!hasAuthSource) {
      return false;
    }

    // The Aura API is a separate origin whose non-streaming data path flows through this
    // interceptor, so attach the same bearer token when a request targets the Aura base URL.
    const auraBaseUrl = environment.aura.apiBaseUrl.trim();
    if (auraBaseUrl && requestUrl.startsWith(auraBaseUrl)) {
      return true;
    }

    // Only attach to API requests, not to external URLs
    const apiBaseUrl = environment.apiBaseUrl.trim();
    if (!apiBaseUrl) {
      return this.isSameOriginRequest(requestUrl);
    }

    const isAbsoluteRequest = /^https?:\/\//i.test(requestUrl);
    const isAbsoluteApiBase = /^https?:\/\//i.test(apiBaseUrl);

    if (isAbsoluteRequest && isAbsoluteApiBase) {
      return requestUrl.startsWith(apiBaseUrl);
    }

    if (!isAbsoluteRequest && !isAbsoluteApiBase) {
      return requestUrl.startsWith(apiBaseUrl);
    }

    if (isAbsoluteRequest && !isAbsoluteApiBase && this.isBrowser) {
      const normalisedApiBase = this.browserEnvironment.resolveUrl(apiBaseUrl);
      return normalisedApiBase ? requestUrl.startsWith(normalisedApiBase) : false;
    }

    return false;
  }

  private isSameOriginRequest(requestUrl: string): boolean {
    const normalisedRequestUrl = requestUrl.trim();
    if (!normalisedRequestUrl) {
      return false;
    }

    const isAbsoluteRequest = /^https?:\/\//i.test(normalisedRequestUrl);
    if (!isAbsoluteRequest) {
      return true;
    }

    return this.getSameOriginPathname(normalisedRequestUrl) !== null;
  }

  private getSameOriginPathname(absoluteUrl: string): string | null {
    const origin = this.browserEnvironment.origin;
    if (!origin) {
      return null;
    }

    try {
      const parsed = new URL(absoluteUrl);
      if (parsed.origin !== origin) {
        return null;
      }

      return parsed.pathname;
    } catch {
      return null;
    }
  }
  private async initialiseInternal(): Promise<void> {
    const embeddedToken = await this.getEmbeddedHostToken();

    if (embeddedToken) {
      this.externalToken = embeddedToken;
      this.isAuthenticated.set(true);
    } else {
      this.isAuthenticated.set(this.isEnabled());
    }

    this.isInitialised = true;
  }

  private async getEmbeddedHostToken(): Promise<string | null> {
    if (!this.isBrowser || !this.hostApplicationService.isEmbedded) {
      return null;
    }

    const existingToken = this.hostApplicationService.currentToken();
    if (existingToken) {
      return existingToken;
    }

    return this.waitForEmbeddedHostToken();
  }

  /**
   * Poll the embedded host until it supplies a token or the timeout elapses.
   *
   * The host may not have registered its post-robot handlers immediately after
   * a browser refresh, so early requests can resolve empty or reject.
   *
   * @returns The host token, or `null` when none is supplied before the timeout.
   */
  private async waitForEmbeddedHostToken(): Promise<string | null> {
    const deadline = Date.now() + EMBEDDED_TOKEN_TIMEOUT_MS;

    for (;;) {
      if (this.externalToken) {
        return this.externalToken;
      }

      try {
        const token = await this.hostApplicationService.getUserToken();
        if (token) {
          return token;
        }
      } catch (error) {
        console.debug('[Auth] Host token request failed, retrying', error);
      }

      if (this.externalToken) {
        return this.externalToken;
      }

      if (Date.now() >= deadline) {
        console.warn('[Auth] Host application did not supply a token before the timeout');
        return null;
      }

      await new Promise<void>((resolve) => setTimeout(resolve, EMBEDDED_TOKEN_RETRY_MS));
    }
  }

}